import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Sheet, AlertTriangle, Car, FileText, Loader2 } from "lucide-react";
import api from "../lib/api";

const TABS = [
  { id: "hoy", label: "Hoy", view: "today" },
  { id: "semana", label: "Semana", view: "week" },
  { id: "mes", label: "Mes", view: "month" },
  { id: "locales", label: "Locales", view: "all" },
  { id: "pr", label: "PR/Promotores", view: "all" },
  { id: "herramientas", label: "Herramientas", view: "all" },
  { id: "problemas", label: "Problemas", view: "all" },
  { id: "competidores", label: "Competidores", view: "all" },
  { id: "oportunidades", label: "Oportunidades", view: "all" },
  { id: "fuentes", label: "Fuentes", view: "all" },
];

export const Badge = ({ c }) => (
  <span
    className={`inline-block text-[10px] font-mono2 uppercase tracking-wider px-1.5 py-0.5 rounded ${
      c === "INFERENCE" ? "badge-inferencia" : "badge-hecho"
    }`}
  >
    {c === "INFERENCE" ? "INFERENCIA" : "HECHO"}
  </span>
);

function Kpi({ icon: Icon, label, value, color, testid }) {
  return (
    <div className="card-glow rounded-lg p-4 sm:p-5 animate-fade-up" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-mono2 uppercase tracking-widest text-slate-500">{label}</span>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <div className="mt-2 text-3xl font-extrabold" style={{ fontFamily: "Syne, sans-serif", color }} data-testid={`${testid}-value`}>
        {value}
      </div>
    </div>
  );
}

function RankedList({ items, testid, empty = "Sin datos todavía" }) {
  if (!items?.length) return <p className="text-sm text-slate-500 py-6 text-center" data-testid={`${testid}-empty`}>{empty}</p>;
  const max = items[0]?.count || 1;
  return (
    <ul className="space-y-2.5" data-testid={testid}>
      {items.map((it, i) => (
        <li key={i} className="group">
          <div className="flex justify-between text-sm mb-1">
            <span className="text-slate-200 truncate">{it.value}</span>
            <span className="font-mono2 text-xs text-slate-500 ml-3 shrink-0">{it.count}</span>
          </div>
          <div className="h-1 rounded-full bg-[#1a1d29] overflow-hidden">
            <div className="h-full bg-[#8b5cf6] rounded-full transition-all duration-500" style={{ width: `${(it.count / max) * 100}%` }} />
          </div>
        </li>
      ))}
    </ul>
  );
}

function ItemList({ items, testid }) {
  if (!items?.length) return <p className="text-sm text-slate-500 py-6 text-center" data-testid={`${testid}-empty`}>Sin datos todavía</p>;
  return (
    <ul className="space-y-2" data-testid={testid}>
      {items.map((it, i) => (
        <li key={i} className="card-glow rounded-md p-3 flex items-start gap-3">
          <div className="flex-1 text-sm text-slate-200">{it.value}</div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            <Badge c={it.classification} />
            <span className="font-mono2 text-[10px] text-slate-500">{it.report_date}</span>
          </div>
        </li>
      ))}
    </ul>
  );
}

export default function Dashboard() {
  const [tab, setTab] = useState("hoy");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const active = TABS.find((t) => t.id === tab);
  const activeView = active.view;

  useEffect(() => {
    setLoading(true);
    api
      .get("/observatory/stats", { params: { view: activeView } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [activeView]);

  const isPeriod = ["hoy", "semana", "mes"].includes(tab);

  return (
    <div data-testid="dashboard-page">
      <div className="mb-6">
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Panel del observatorio</h1>
        <p className="text-sm text-slate-500 mt-1">Análisis diario del nightlife de Milán — datos reales ingeridos desde Make</p>
      </div>

      <div className="flex gap-1.5 overflow-x-auto pb-2 mb-6 -mx-1 px-1" data-testid="dashboard-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`tab-${t.id}`}
            className={`px-3.5 py-2 rounded-md text-xs font-medium whitespace-nowrap transition-colors ${
              tab === t.id ? "bg-[#8b5cf6] text-white" : "bg-[#12141d] text-slate-400 hover:text-white border border-[#232838]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20" data-testid="dashboard-loading">
          <Loader2 className="w-6 h-6 animate-spin text-[#8b5cf6]" />
        </div>
      ) : !data ? (
        <p className="text-sm text-red-400" data-testid="dashboard-error">Error al cargar estadísticas.</p>
      ) : data.total_reports === 0 && isPeriod ? (
        <div className="card-glow rounded-lg p-10 text-center animate-fade-up" data-testid="dashboard-empty">
          <FileText className="w-8 h-8 text-slate-600 mx-auto mb-3" />
          <p className="text-sm text-slate-400">Sin informes en este período.</p>
          <p className="text-xs text-slate-600 mt-1">Envía el primer informe desde Make a <span className="font-mono2">POST /api/observatory/reports</span></p>
        </div>
      ) : (
        <div className="space-y-6">
          {isPeriod && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                <Kpi icon={FileText} label="Informes" value={data.total_reports} color="#f8fafc" testid="kpi-informes" />
                <Kpi icon={MessageCircle} label="Menciones WhatsApp" value={data.kpis.whatsapp_mentions} color="#25d366" testid="kpi-whatsapp" />
                <Kpi icon={Sheet} label="Google Sheets / manual" value={data.kpis.google_sheets_mentions} color="#0f9d58" testid="kpi-sheets" />
                <Kpi icon={AlertTriangle} label="Conflictos multicanal" value={data.kpis.multichannel_conflicts} color="#ef4444" testid="kpi-conflictos" />
                <Kpi icon={Car} label="Coordinación NCC" value={data.kpis.ncc_mentions} color="#f59e0b" testid="kpi-ncc" />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="card-glow rounded-lg p-5">
                  <h3 className="text-lg font-semibold mb-4">Locales recurrentes</h3>
                  <RankedList items={data.top_venues} testid="period-locales" />
                </div>
                <div className="card-glow rounded-lg p-5">
                  <h3 className="text-lg font-semibold mb-4">PR / Promotores recurrentes</h3>
                  <RankedList items={data.top_promoters} testid="period-pr" />
                </div>
              </div>
              <div className="card-glow rounded-lg p-5 flex items-center gap-6">
                <div>
                  <div className="text-[10px] font-mono2 uppercase tracking-widest text-slate-500 mb-1">HECHO</div>
                  <div className="text-2xl font-bold text-[#34d399]" data-testid="count-hecho">{data.classifications.FACT}</div>
                </div>
                <div>
                  <div className="text-[10px] font-mono2 uppercase tracking-widest text-slate-500 mb-1">INFERENCIA</div>
                  <div className="text-2xl font-bold text-[#c084fc]" data-testid="count-inferencia">{data.classifications.INFERENCE}</div>
                </div>
              </div>
            </>
          )}

          {tab === "locales" && <div className="card-glow rounded-lg p-5 animate-fade-up"><h3 className="text-lg font-semibold mb-4">Locales</h3><RankedList items={data.top_venues} testid="view-locales" /></div>}
          {tab === "pr" && <div className="card-glow rounded-lg p-5 animate-fade-up"><h3 className="text-lg font-semibold mb-4">PR / Promotores</h3><RankedList items={data.top_promoters} testid="view-pr" /></div>}
          {tab === "herramientas" && <div className="card-glow rounded-lg p-5 animate-fade-up"><h3 className="text-lg font-semibold mb-4">Herramientas y canales</h3><RankedList items={data.tools} testid="view-herramientas" /></div>}
          {tab === "competidores" && <div className="card-glow rounded-lg p-5 animate-fade-up"><h3 className="text-lg font-semibold mb-4">Competidores</h3><RankedList items={data.competitors} testid="view-competidores" /></div>}
          {tab === "fuentes" && <div className="card-glow rounded-lg p-5 animate-fade-up"><h3 className="text-lg font-semibold mb-4">Fuentes</h3><RankedList items={data.sources} testid="view-fuentes" /></div>}
          {tab === "problemas" && <div className="animate-fade-up"><h3 className="text-lg font-semibold mb-4">Problemas operativos</h3><ItemList items={data.problems} testid="view-problemas" /></div>}
          {tab === "oportunidades" && <div className="animate-fade-up"><h3 className="text-lg font-semibold mb-4">Oportunidades de negocio</h3><ItemList items={data.opportunities} testid="view-oportunidades" /></div>}
        </div>
      )}

      <div className="mt-8 text-right">
        <Link to="/informes" className="text-xs text-[#8b5cf6] hover:text-[#c084fc] transition-colors" data-testid="link-archivo">
          Ver archivo completo de informes →
        </Link>
      </div>
    </div>
  );
}
